if ( typeof ShippingNotice !== 'function' ) {

  // Free ground shipping at $199+ is the only cart offer (a $0 "Free Ground Shipping on $199+" rate, checked against
  // the cart total AFTER discounts). The bottle tiers ended: 10% at 6 and 15% at 12 on 2026-10-05, 5% at 4 on 2026-10-13.
  const VOLUME_TIERS = [];
  const PROMO_FREE_CENTS = 19900;
  const promoOn = () => true;
  const WINE_TYPES = [ 'Red Wine', 'White Wine', 'Orange Wine', 'Rosé Wine', 'Rose Wine', 'Sparkling Wine', 'Dessert Wine', 'Piquette' ];
  const wineCount = cart => cart.items.reduce((n, i) => n + ( WINE_TYPES.includes(i.product_type) ? i.quantity : 0 ), 0);

  // Lightweight, dependency-free confetti burst fired when a reward tier unlocks.
  function fireCartConfetti(origin) {
    if ( window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:100000';
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    const resize = () => { canvas.width = innerWidth * dpr; canvas.height = innerHeight * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); };
    resize();

    const colors = ['#E4572E', '#F3A712', '#4CB944', '#2E86AB', '#D7263D', '#F7B32B', '#7B2CBF'];
    const parts = [];
    for ( let i = 0; i < 110; i++ ) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 6 + Math.random() * 8;
      parts.push({
        x: origin.x, y: origin.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 5,
        size: 5 + Math.random() * 6,
        rot: Math.random() * Math.PI,
        vr: (Math.random() - 0.5) * 0.4,
        color: colors[i % colors.length],
        shape: Math.random() < 0.5 ? 'rect' : 'circ',
        life: 0
      });
    }

    const gravity = 0.3, drag = 0.985, maxLife = 95;
    let raf;
    function frame() {
      ctx.clearRect(0, 0, innerWidth, innerHeight);
      let alive = false;
      for ( const p of parts ) {
        p.life++;
        if ( p.life > maxLife ) continue;
        alive = true;
        p.vx *= drag; p.vy = p.vy * drag + gravity;
        p.x += p.vx; p.y += p.vy; p.rot += p.vr;
        ctx.save();
        ctx.globalAlpha = Math.max(0, 1 - p.life / maxLife);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if ( p.shape === 'rect' ) {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }
      if ( alive ) { raf = requestAnimationFrame(frame); }
      else { cancelAnimationFrame(raf); canvas.remove(); }
    }
    raf = requestAnimationFrame(frame);
    setTimeout(() => { try { cancelAnimationFrame(raf); canvas.remove(); } catch (e) {} }, 3000);
  }

  class ShippingNotice extends HTMLElement {

    constructor() {
      super();
      this.init();
    }

    init() {

      const rate = ( window.Shopify && Shopify.currency && Shopify.currency.rate ) ? Number(Shopify.currency.rate) : 1;
      const moneyFmt = ( window.KROWN && KROWN.settings && KROWN.settings.shop_money_format ) ? KROWN.settings.shop_money_format : '${{amount}}';

      this.moneyFmt = moneyFmt;

      // The reward is counted in BOTTLES now, not dollars: a full case ships free.
      this.freeShipBottles = Number(this.getAttribute('data-free-bottles')) || 12;

      // Server-rendered cart weight (grams) for an instant first paint.
      const seedBottles = this._bottles(this.getAttribute('data-cart-weight'));

      this._injectStyles();
      this._renderBar(seedBottles, false, null, Number(this.getAttribute('data-cart-total-amount')));

      // Pull authoritative line items to refine the bar.
      const cartRoot = ( window.KROWN && KROWN.settings && KROWN.settings.routes ) ? KROWN.settings.routes.cart_url : '/cart';
      fetch(`${cartRoot}.js`, { headers: { 'Accept': 'application/json' } })
        .then(r => r.json())
        .then(cart => {
          // Only the primary (gift-managing) bar performs cleanup, and only once.
          if ( !this.hasAttribute('data-no-gift') ) this._cleanupLegacyGift(cart);
          this._renderBar(this._bottles(cart.total_weight), true, wineCount(cart), cart.total_price);
        })
        .catch(() => { /* keep the seeded bar; never block the cart */ });
    }

    /* ---------- keep mirror bars in sync ----------
       A display-only bar (data-no-gift) that lives OUTSIDE the AJAX-replaced
       subtotal region — e.g. the copy pinned to the top of the cart page on
       mobile — is never re-created on a cart change, so on its own it would show
       a stale total. Watch the subtotal region and re-render when it updates. */
    connectedCallback() {
      if ( this._wired || !this.hasAttribute('data-no-gift') ) return;
      const subtotal = document.getElementById('AjaxCartSubtotal');
      if ( !subtotal || subtotal.contains(this) ) return;
      this._wired = true;
      this._mo = new MutationObserver(() => this._refresh());
      this._mo.observe(subtotal, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      if ( this._mo ) { this._mo.disconnect(); this._mo = null; }
      this._wired = false;
    }

    _refresh() {
      const cartRoot = ( window.KROWN && KROWN.settings && KROWN.settings.routes ) ? KROWN.settings.routes.cart_url : '/cart';
      fetch(`${cartRoot}.js`, { headers: { 'Accept': 'application/json' } })
        .then(r => r.json())
        .then(cart => this._renderBar(this._bottles(cart.total_weight), true, wineCount(cart), cart.total_price))
        .catch(() => {});
    }

    /* ---------- legacy cleanup ----------
       The free ice-pack gift has been retired. Any cart that still carries the
       old auto-added line gets it silently removed, once per page session. */
    _cleanupLegacyGift(cart) {
      if ( window.__iceCleanupDone ) return;
      window.__iceCleanupDone = true;
      const LEGACY_ICE_VARIANT = 50566383337688;
      const gift = cart.items.find(i => i.variant_id === LEGACY_ICE_VARIANT);
      if ( !gift ) return;
      const url = ( window.KROWN && KROWN.settings && KROWN.settings.routes ) ? KROWN.settings.routes.cart_change_url : '/cart/change';
      fetch(`${url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: gift.key, quantity: 0 })
        })
        .then(() => { if ( typeof window.refreshCart === 'function' ) window.refreshCart(false); })
        .catch(() => {});
    }

    /* ---------- rendering ---------- */

    _bottles(grams) {
      // A bottle is 3 lb. Shopify stores grams, and 3 lb rounds to 1361 g, so divide and round.
      return Math.round((Number(grams) || 0) / 1360.777);
    }

    _renderBar(bottles, animate, wineBottles, totalCents) {

      const promo = promoOn();
      const target = promo ? 6 : this.freeShipBottles;
      const filled = Math.max(0, Math.min(bottles, target));
      const left = Math.max(0, target - bottles);
      const unlocked = bottles >= target;
      // Volume tiers count wine bottles only (the automatic discounts are scoped to wine types).
      // Before cart.js arrives the weight count stands in.
      const wine = wineBottles == null ? bottles : wineBottles;
      const tiers = VOLUME_TIERS.filter(t => t.at <= target);
      const reached = tiers.filter(t => wine >= t.at).pop();
      const next = tiers.find(t => wine < t.at);
      const plural = n => `${n} more bottle${n === 1 ? '' : 's'}`;

      const top = tiers[tiers.length - 1];
      let message;
      if ( next ) {
        message = `${reached ? `<span class="cart-reward__done">${reached.label} unlocked.</span> ` : ''}<b>${plural(next.at - wine)}</b> for ${next.label}`;
      } else if ( reached ) {
        message = `<span class="cart-reward__done">${reached.label} unlocked</span>`;
      } else {
        message = `<b>${plural(Math.max(0, (top ? top.at : target) - wine))}</b> for ${top ? top.label : '15% off'}`;
      }

      // Tags sit in their own row of cells matching the dot row, so a dot's opacity never fades its label.
      // The bar tracks dollars toward free ground shipping.
      const promoBar = promo && Number.isFinite(totalCents);
      if ( promoBar ) {
        const short = PROMO_FREE_CENTS - totalCents;
        const ship = short > 0
          ? `<b>${this._formatMoney(short, this.moneyFmt)} more</b> for free ground shipping`
          : `<span class="cart-reward__done">Free ground shipping unlocked</span>`;
        message = ship;
      }

      const labelAt = n => (tiers.find(t => t.at === n) || {}).label;
      let dots = '', tags = '';
      for ( let i = 0; i < target; i++ ) {
        const n = i + 1;
        const label = labelAt(n);
        // The dot that just filled is flagged so it can pop rather than simply appear.
        const isNew = animate && i === filled - 1;
        dots += `<span class="cart-reward__dot${label ? ' is-reward' : ''}${i < filled ? ' is-filled' : ''}${isNew ? ' is-new' : ''}"></span>`;
        tags += `<span class="cart-reward__cell">${label ? `<span class="cart-reward__tag${n === target ? ' is-end' : ''}">${label}</span>` : ''}</span>`;
      }

      if ( promoBar ) {
        const pct = Math.max(0, Math.min(100, totalCents / PROMO_FREE_CENTS * 100));
        const from = typeof window.__cartShipPct === 'number' ? window.__cartShipPct : pct;
        const shipUnlocked = totalCents >= PROMO_FREE_CENTS;
        this.innerHTML = `
          <div class="cart-reward${shipUnlocked ? ' is-unlocked' : ''}" role="status" aria-live="polite">
            <p class="cart-reward__msg">${message}</p>
            <div class="cart-reward__track-tags" aria-hidden="true"><span>$0</span><span>${this._formatMoney(PROMO_FREE_CENTS, this.moneyFmt).replace(/\.00$/, '')}</span></div>
            <div class="cart-reward__track" aria-hidden="true"><span class="cart-reward__fill" style="width:${from}%"></span></div>
          </div>`;
        const fill = this.querySelector('.cart-reward__fill');
        requestAnimationFrame(() => requestAnimationFrame(() => { fill.style.width = pct + '%'; }));
        window.__cartShipPct = pct;
        if ( animate ) {
          if ( window.__cartRewardReached === false && shipUnlocked ) this._celebrate();
          window.__cartRewardReached = shipUnlocked;
        }
        return;
      }

      this.innerHTML = `
        <div class="cart-reward${unlocked ? ' is-unlocked' : ''}" role="status" aria-live="polite">
          <p class="cart-reward__msg">${message}</p>
          <div class="cart-reward__dots cart-reward__tags" aria-hidden="true">${tags}</div>
          <div class="cart-reward__dots" aria-hidden="true">${dots}</div>
        </div>`;

      // Celebrate the twelfth bottle. Never on the first paint of a page session, so the
      // confetti marks a real crossing rather than arriving on a cart that was already full.
      if ( animate ) {
        if ( window.__cartRewardReached === false && unlocked ) this._celebrate();
        window.__cartRewardReached = unlocked;
      }
    }

    _celebrate() {
      const el = this.querySelector('.cart-reward__dots') || this;
      const r = el.getBoundingClientRect();
      const origin = ( r && r.width )
        ? { x: r.left + r.width / 2, y: r.top + r.height / 2 }
        : { x: window.innerWidth / 2, y: window.innerHeight / 3 };
      fireCartConfetti(origin);
    }

    _injectStyles() {
      if ( document.getElementById('cart-reward-styles') ) return;
      const style = document.createElement('style');
      style.id = 'cart-reward-styles';
      style.textContent = `
        shipping-notice.cart-notice{display:block}
        .cart-reward{width:100%;box-sizing:border-box;padding:4px 9px 24px}
        .cart-reward{--cart-reward-accent:#FFD43B}
        .cart-reward__msg{margin:0 0 10px;font-size:11.5px;line-height:1.3;text-align:center;text-wrap:balance}
        .cart-reward__msg b{font-weight:700}
        .cart-reward__done{font-weight:700}
        .cart-reward__dots{display:flex;gap:5px;align-items:center;justify-content:center}
        .cart-reward__dot{flex:1 1 0;max-width:22px;height:9px;border-radius:999px;box-sizing:border-box;
          border:1px solid currentColor;opacity:.28;transition:opacity .35s ease,background-color .35s ease,transform .35s ease}
        .cart-reward__dot.is-filled{opacity:1;background:currentColor}
        .cart-reward__dot.is-new{animation:cartRewardPop .45s cubic-bezier(.34,1.56,.64,1)}
        .cart-reward.is-unlocked .cart-reward__dot{opacity:1;background:currentColor}
        .cart-reward__tags{margin-bottom:5px}
        .cart-reward__cell{flex:1 1 0;max-width:22px;height:13px;position:relative}
        .cart-reward__tag{position:absolute;bottom:0;left:50%;transform:translateX(-50%);font-size:10.5px;line-height:1;font-weight:700;white-space:nowrap;color:var(--cart-reward-accent)}
        .cart-reward__tag.is-end{left:auto;right:0;transform:none}
        .cart-reward .cart-reward__dot.is-reward{border-color:var(--cart-reward-accent);opacity:.7}
        .cart-reward .cart-reward__dot.is-reward.is-filled{background:var(--cart-reward-accent);opacity:1}
        .cart-reward__track-tags,.cart-reward__track{max-width:none;width:100%;margin:0 auto}
        .cart-reward__track-tags{display:flex;justify-content:space-between;margin-bottom:5px;font-size:10.5px;line-height:1;font-weight:700;color:var(--cart-reward-accent)}
        .cart-reward__track{height:9px;border-radius:999px;border:1px solid var(--cart-reward-accent);box-sizing:border-box;overflow:hidden}
        .cart-reward__fill{display:block;height:100%;background:var(--cart-reward-accent);transition:width .6s cubic-bezier(.45,.05,.55,.95)}
        @media (prefers-reduced-motion: reduce){.cart-reward__fill{transition:none}}
        @keyframes cartRewardPop{0%{transform:scale(.4)}60%{transform:scale(1.35)}100%{transform:scale(1)}}
        @media (prefers-reduced-motion: reduce){.cart-reward__dot{transition:none}.cart-reward__dot.is-new{animation:none}}
      `;
      document.head.appendChild(style);
    }

    /* ---------- money ---------- */

    _formatMoney(cents, format) {

      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }

      let value = '';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || '${{amount}}';

      function formatWithDelimiters(number, precision, thousands, decimal) {
        thousands = thousands || ',';
        decimal = decimal || '.';

        if (isNaN(number) || number === null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(
          /(\d)(?=(\d\d\d)+(?!\d))/g,
          '$1' + thousands
        );
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        return dollarsAmount + centsAmount;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'");
          break;
      }

      return formatString.replace(placeholderRegex, value);

    }

  }

  if ( typeof customElements.get('shipping-notice') == 'undefined' ) {
    customElements.define('shipping-notice', ShippingNotice);
  }

}
